if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        this.querySelector('.country-selector__overlay')?.addEventListener('click', () => {
          this.hidePanel();
          this.focusBack();
        });

        if (this.elements.search) {
          // Atelier: 'input', not 'keyup', so dictated, pasted and autofilled text filters too (at-header.js writes the
          // no-match line on the same event).
          this.elements.search.addEventListener('input', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', () => {
            this.hidePanel();
            this.focusBack();
          });
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      // Atelier: removed with the panel open (the theme editor re-renders the header): its body lock goes with it.
      disconnectedCallback() {
        if (this.elements.button.getAttribute('aria-expanded') === 'true') this.hidePanel();
      }

      hidePanel() {
        this.setModal(false);
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.setAttribute('aria-expanded', 'false');
          this.elements.search.value = '';
          this.filterCountries();
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        document.body.classList.remove('at-currency-open');
        this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        if (event.defaultPrevented) return;
        // Atelier: only rows that are showing. A search hides the popular list itself but not its rows, so the arrows
        // moved focus to those invisible copies (and so nowhere); with no match there is no row at all.
        const focusableItems = Array.from(this.querySelectorAll('a')).filter((item) => item.getClientRects().length);
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus?.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus?.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.focusBack();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      // Atelier: focus goes back to what opened the selector (the phone menu's or the footer's currency trigger,
      // assets/at-header.js) when that still shows, else to the header button, else stays put: on phones the header
      // button is display:none, so focusing it dropped keyboard focus to the page.
      focusBack() {
        const back = [this.opener, this.elements.button].find((el) => el?.isConnected && el.getClientRects().length);
        back?.focus();
      }

      openSelector() {
        const from = document.activeElement;
        this.opener = from && from !== document.body && !this.contains(from) ? from : null;
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.elements.search) {
          this.elements.search.setAttribute('aria-expanded', !this.elements.panel.hasAttribute('hidden'));
          this.filterCountries();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
        // Atelier: the Klaviyo teaser steps aside while the list is open (at-klaviyo.css); on desktop it overlapped the
        // dropdown's right edge.
        document.body.classList.toggle('at-currency-open', !this.elements.panel.hasAttribute('hidden'));
        this.setModal(!this.elements.panel.hasAttribute('hidden'));
      }

      // Atelier: on phones the list is a bottom sheet over the page, a modal dialog (at-currency.liquid): the page around
      // the selector is inert while it is open (global.js inertPage) and Tab stays in the sheet (trapFocus; WebKit tabbed
      // from the search field straight out of it, which closed it), both released before focus goes back to the opener.
      // From 801px it is a dropdown under the header button and the page stays as it is.
      setModal(open) {
        const panel = this.elements.panel;
        const sheet = open && window.matchMedia('(max-width: 800px)').matches;
        if (sheet) panel.setAttribute('aria-modal', 'true');
        else panel.removeAttribute('aria-modal');
        if (typeof inertPage !== 'function') return;
        if (sheet) {
          inertPage(this, { isOpen: () => !panel.hasAttribute('hidden'), close: () => this.hidePanel() });
          trapFocus(panel, panel.contains(document.activeElement) ? document.activeElement : this.elements.closeButton || panel);
          this.trapped = true;
          // Turned sideways past 800px the sheet would become the dropdown with the page still set aside: it closes.
          this.onPhone ||= (e) => !e.matches && this.hidePanel();
          this.mql800 ||= window.matchMedia('(max-width: 800px)');
          this.mql800.addEventListener('change', this.onPhone);
        } else {
          this.mql800?.removeEventListener('change', this.onPhone);
          releasePage(this);
          if (this.trapped) removeTrapFocus();
          this.trapped = false;
        }
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        // Atelier: trimmed, so spaces alone show the whole list and 'australia ' still finds Australia.
        const searchValue = this.normalizeString(this.elements.search.value.trim());
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        const exactCountry = [...allCountries].some((item) => this.normalizeString(item.querySelector('.country').textContent) === searchValue);
        this.setActiveCountry(null);
        // Atelier: counts the matching rows outside the popular list (Dawn started at the total and moved it by one
        // per row, so 2 matches were announced as "6 countries/regions found").
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(exactCountry
            ? item.querySelector('.country').textContent
            : item.textContent + ' ' + (item.dataset.currencyName || ''));
          if (exactCountry ? countryName === searchValue : countryName.includes(searchValue)) {
            item.parentElement.classList.remove('hidden');
            if (!item.closest('.popular-countries')) visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion) {
          const countText = (visibleCountries === 1
            ? this.elements.liveRegion.dataset.one
            : this.elements.liveRegion.dataset.other) || window.accessibilityStrings.countrySelectorSearchCount;
          this.elements.liveRegion.textContent = countText.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          const id = this.elements.search.getAttribute('aria-activedescendant');
          if (id) this.querySelector(`#${id}`)?.click();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const rows = [...this.querySelectorAll('[role="option"]')].filter((row) => row.getClientRects().length);
          const index = rows.findIndex((row) => row.id === this.elements.search.getAttribute('aria-activedescendant'));
          let next = Math.min(index + 1, rows.length - 1);
          if (event.key === 'ArrowUp') next = index < 0 ? rows.length - 1 : Math.max(0, index - 1);
          this.setActiveCountry(rows[next]);
          rows[next]?.scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home' || event.key === 'End') {
          this.setActiveCountry(null);
        }
      }

      // The combobox keeps DOM focus; aria-activedescendant moves the screen reader and the visible selection together.
      setActiveCountry(row) {
        this.querySelectorAll('[role="option"][aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
        if (row) {
          row.setAttribute('aria-selected', 'true');
          this.elements.search.setAttribute('aria-activedescendant', row.id);
        } else {
          this.elements.search.removeAttribute('aria-activedescendant');
        }
      }
    }
  );
}
